import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ApiClient, useApiClient } from './apiClient';
import { loadFailureOf } from './loadFailure';

/**
 * Generic shape returned by every `use{Resource}` data hook.
 * Consumers destructure what they need — mirroring RTK Query / react-query style
 * without pulling in a dependency.
 */
export interface UseApiResourceResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /**
   * The failed request's HTTP status and the API's `code` (`DB_BUSY`,
   * `DB_TIMEOUT`, `REQUEST_TIMEOUT`), `null` when there is no error or the
   * server gave none. `error` stays the server's message for its 80-odd readers;
   * a panel that shows a failure branches on these instead (`ReportLoadError`).
   */
  errorStatus: number | null;
  errorCode: string | null;
  refetch: () => Promise<void>;
  /**
   * Escape hatch for optimistic updates / local invalidation without a refetch.
   * Use sparingly — prefer `refetch()` after mutations unless the server
   * response is already in hand.
   */
  setData: React.Dispatch<React.SetStateAction<T | null>>;
}

export interface UseApiResourceOptions {
  /**
   * When true, suppresses the automatic fetch on mount / when deps change.
   * Useful when the request requires a prerequisite (e.g. a selected employee ID)
   * that may be `null` on first render.
   */
  skip?: boolean;
  /**
   * Human-readable prefix used when the fetcher throws a non-Error value.
   * Defaults to `"Failed to load resource"`.
   */
  errorPrefix?: string;
  /**
   * Injection point for tests and edge cases where the default
   * `useApiClient(getAccessToken)` isn't appropriate.
   */
  apiClientOverride?: ApiClient;
}

/**
 * Fetch a resource with race-condition-safe state management.
 *
 * - `fetcher` receives the memoised `ApiClient` and returns the parsed resource.
 * - `deps` controls when the fetch re-runs. Treat it the same way you'd treat a
 *   `useEffect` deps array. Include every value referenced inside `fetcher`.
 * - Stale responses are discarded via an incrementing fetch ID, so late arrivals
 *   from a previous `deps` never clobber the current UI state.
 *
 * @example
 * const { data: activities, loading, error, refetch } = useApiResource(
 *   (client) => client.get<TimesheetActivity[]>(
 *     projectId ? `/timesheet-activities?projectId=${projectId}` : '/timesheet-activities',
 *   ),
 *   [projectId],
 * );
 */
export function useApiResource<T>(
  fetcher: (apiClient: ApiClient) => Promise<T>,
  deps: ReadonlyArray<unknown>,
  options: UseApiResourceOptions = {},
): UseApiResourceResult<T> {
  const { skip = false, errorPrefix = 'Failed to load resource', apiClientOverride } = options;

  const { getAccessToken } = useAuth();
  const defaultApiClient = useApiClient(getAccessToken);
  const apiClient = apiClientOverride ?? defaultApiClient;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(!skip);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetcherRef = useRef(fetcher);
  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  const runFetch = useCallback(async () => {
    if (skip) {
      setLoading(false);
      return;
    }
    const currentFetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    setErrorCode(null);

    try {
      const value = await fetcherRef.current(apiClient);
      if (currentFetchId !== fetchIdRef.current) return;
      setData(value);
    } catch (err) {
      if (currentFetchId !== fetchIdRef.current) return;
      const { status, code } = loadFailureOf(err);
      setError(err instanceof Error ? err.message : errorPrefix);
      setErrorStatus(status);
      setErrorCode(code);
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [apiClient, errorPrefix, skip]);

  useEffect(() => {
    void runFetch();
    // `runFetch` is stable w.r.t apiClient; deps are user-supplied and drive refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runFetch, ...deps]);

  const refetch = useCallback(async () => {
    await runFetch();
  }, [runFetch]);

  return { data, loading, error, errorStatus, errorCode, refetch, setData };
}
